import {
  ArrowLeftIcon,
  Bell,
  Building2,
  ChevronDown,
  KeyRound,
  LogOut,
  User,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Link, useNavigate } from "react-router-dom";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";
import { useDisplayNameStore } from "../store/useDisplayNameStore";
import { useIsDetailPage } from "../hooks/useIsDetailPage";
import { Notification, useNotifications } from "../hooks/useNotifications";
import { NotificationPopover } from "./NotificationPopover";

interface HeaderProps {
  onBack?: () => void;
}

export default function Header({ onBack }: HeaderProps) {
  const navigate = useNavigate();
  const { logout: signOut, user } = useAuth() as AuthContextValue;
  const { notifications, unreadCount, markAsSeen } = useNotifications();
  const { isDetail, backPath } = useIsDetailPage();
  const title = useDisplayNameStore((s) => s.displayName);

  const handleNotificationClick = (n: Notification) => {
    markAsSeen(n.messageId);
    navigate(`/voucher/${n.id}`);
  };

  const handleLogout = () => {
    const token = localStorage.getItem("token");

    if (token) {
      signOut();
      localStorage.removeItem("token");
      navigate("/");
    }
  };

  const displayName = user?.fullName || "John Doe";
  const displayRole = user?.userName || "Admin";

  const hideOrganizationMenu =
    user?.roleName === "ROLE_KFT_SUPER_ADMIN" ||
    user?.roleName === "ROLE_KFT_SP_ADMIN";

  return (
    <header className="sticky top-0 z-50 flex items-center justify-between bg-[#F5F5F5] px-6 py-3 m-3 rounded-2xl">
      {/* Left: Back + Title */}
      <div className="flex items-center space-x-2">
        {isDetail && (
          <Link
            to={backPath}
            className="flex items-center border border-transparent p-2 text-gray-900 hover:text-gray-900 hover:border hover:bg-gray-200 hover:rounded-full"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-1" />
          </Link>
        )}
        <h1 className="text-xl font-medium text-gray-800">
          {title} {isDetail ? "Details" : ""}
        </h1>
      </div>

      {/* Right: Notification + User */}
      <div className="flex items-center gap-4">
        <NotificationPopover
          triggerComponent={
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-full hover:bg-gray-100 bg-white"
            >
              <Bell className="h-5 w-5 text-gray-700" />

              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-[10px] text-white font-semibold">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          }
          notifications={notifications}
          unreadCount={unreadCount}
          onNotificationClick={handleNotificationClick}
          onSeeMoreClick={() => navigate("/voucher")}
        />

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-2 cursor-pointer">
              <Avatar className="w-10 h-10">
                {user?.avatarUrl ? (
                  <AvatarImage src={user.avatarUrl} alt={user.fullName} />
                ) : (
                  <AvatarFallback className="text-gray-900 font-semibold border border-gray-300 flex justify-center items-center">
                    {(user?.fullName || "U").charAt(0).toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>

              <div className="text-left">
                <p className="text-sm font-semibold leading-none text-gray-950">
                  {displayName}
                </p>
                <p className="text-xs text-gray-950/60">@{displayRole}</p>
              </div>

              <ChevronDown className="h-4 w-4 text-gray-500" />
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="w-48 bg-white flex flex-col gap-y-1 p-2 shadow-lg"
          >
            <DropdownMenuItem
              onClick={() => navigate("/profile")}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100"
            >
              <User className="w-4 h-4" />
              My Profile
            </DropdownMenuItem>

            {user?.mfiId && !hideOrganizationMenu ? (
              <DropdownMenuItem
                onClick={() => navigate("/organization")}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-100"
              >
                <Building2 className="w-4 h-4" />
                Organization
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              onClick={() => navigate("/changePassword")}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100"
            >
              <KeyRound className="w-4 h-4" />
              Change Password
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleLogout}
              className="flex items-center gap-2 cursor-pointer text-red-600 hover:bg-gray-100"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
